import { useIonToast } from '@ionic/react';
import { useCallback } from 'react';
import { LocalizedTextField, i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { logger } from '../../../../../util/logger';
import { FlowItem } from '../../../shared/model/flow.types';
import { FlowItemService } from '../../../shared/service/service.types';
import { instanceOfFlowItemSection } from '../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../model/beacon-welding-data';

interface ValidateItemBase {
  isValid: boolean;
}

interface ValidateItemSuccess extends ValidateItemBase {
  isValid: true;
}

interface ValidateItemError extends ValidateItemBase {
  isValid: false;
  // This error message may be passed the item label as a parameter for localization
  errorMessage?: i18nKeyType | LocalizedTextField;
}

type ValidateItemResult = ValidateItemSuccess | ValidateItemError;

interface ValidatePathParams {
  flowItemService: FlowItemService<BeaconWeldingData.FlowConfig>;
  items: FlowItem[];
  scratchpadId: string | undefined;
  generalErrorMessageKey: i18nKeyType | LocalizedTextField;
  validateItem: (item: FlowItem) => ValidateItemResult;
}

type ValidatePath = (startItem: FlowItem) => boolean;

const useValidatePath = (params: ValidatePathParams): ValidatePath => {
  const {
    flowItemService,
    items,
    scratchpadId,
    generalErrorMessageKey,
    validateItem,
  } = params;
  const [presentValidationError] = useIonToast();
  const flowItemLabels = flowItemService.buildFlowItemTextArr(items);

  const validatePath = useCallback(
    (startItem: FlowItem): boolean => {
      if (instanceOfFlowItemSection(startItem)) {
        return true;
      }

      const startIndex = items.findIndex((i) => i.id === startItem.id);
      const endIndex = flowItemService.getEndOfSequenceIndexFromFlowItem(
        startItem,
        items
      );

      if (endIndex === undefined) {
        logger.error('Invalid path. No end index found.', {
          scratchpadId,
          startItem,
        });

        presentValidationError({
          color: 'danger',
          duration: 3500,
          message: getLocalizedText(generalErrorMessageKey),
          position: 'bottom',
        });

        return false;
      }

      for (let i = startIndex; i < endIndex + 1; i++) {
        const item = items[i];

        const result = validateItem(item);

        if (result.isValid) {
          continue;
        } else {
          const itemLabel = flowItemLabels[i];
          const errorMessage = itemLabel
            ? getLocalizedText(result?.errorMessage ?? generalErrorMessageKey, {
                itemLabel: itemLabel?.label,
              })
            : getLocalizedText('welding:convert_to_stitch_fatal_error');

          presentValidationError({
            color: 'danger',
            duration: 3500,
            message: errorMessage,
            position: 'bottom',
          });

          return false;
        }
      }

      return true;
    },
    [
      flowItemLabels,
      flowItemService,
      generalErrorMessageKey,
      items,
      presentValidationError,
      scratchpadId,
      validateItem,
    ]
  );

  return validatePath;
};

export { useValidatePath };
